import React, { useState, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  useApplyComparisonSelectionMutation,
  useCreateSupplierCostMappingMutation,
  useGetPurchaseRequestByIdQuery,
  useGetSuppliersQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface SupplierRevision {
  id: number;
  file_name: string;
  quotations: {
    [productId: string]: {
      unit_price: number;
      total_cost: number;
      delivery_days?: number;
      notes?: string;
    };
  };
}

interface SupplierQuotation {
  supplier_id: number;
  supplier_name: string;
  email: string;
  revisions: SupplierRevision[];
}

interface Product {
  id: number;
  product_name: string;
  quantity: number;
  measurement_name: string;
  expected_delivery_date?: string;
}

interface SupplierComparisonModalProps {
  isOpen: boolean;
  toggle: () => void;
  rfqItems: Product[];
  purchaseRequestNumber: string;
  purchaseRequestId?: string | number;
  selectedSupplierIds?: number[]; // Suppliers who received RFQ emails
}

const SupplierComparisonModal: React.FC<SupplierComparisonModalProps> = ({
  isOpen,
  toggle,
  rfqItems,
  purchaseRequestNumber,
  purchaseRequestId,
  selectedSupplierIds = [],
}) => {
  const [suppliers, setSuppliers] = useState<SupplierQuotation[]>([]);
  const [selectedSupplierForImport, setSelectedSupplierForImport] = useState<number | null>(null);
  const [selectedSuppliersForComparison, setSelectedSuppliersForComparison] = useState<Set<number>>(
    new Set()
  );
  const [selectedProductSupplierMap, setSelectedProductSupplierMap] = useState<{
    [productId: number]: number;
  }>({});
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importStep, setImportStep] = useState<"select_supplier" | "import" | "compare">(
    "select_supplier"
  );
  const [isSavingMappings, setIsSavingMappings] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API hooks
  const [createSupplierCostMapping] = useCreateSupplierCostMappingMutation();
  const [applyComparisonSelection] = useApplyComparisonSelectionMutation();
  const {
    data: purchaseRequestData,
    refetch: refetchPurchaseRequest,
  } = useGetPurchaseRequestByIdQuery(purchaseRequestId?.toString() || "", {
    skip: !purchaseRequestId || !isOpen,
  });
  const { data: allSuppliersData } = useGetSuppliersQuery(
    { page: 1, pageSize: 100, search: supplierSearch },
    {
      skip: !isOpen,
    }
  );

  // Extract suppliers who received RFQ emails
  // Priority: 1) selectedSupplierIds prop, 2) Check purchase request items, 3) Show all suppliers
  const availableSuppliers = React.useMemo(() => {
    if (!allSuppliersData) return [];

    const suppliersList = Array.isArray(allSuppliersData)
      ? allSuppliersData
      : allSuppliersData?.results || [];

    // If selectedSupplierIds prop is provided, use those
    if (selectedSupplierIds && selectedSupplierIds.length > 0) {
      return suppliersList
        .filter((supplier: any) => selectedSupplierIds.includes(supplier.id))
        .map((supplier: any) => ({
          id: supplier.id,
          name: supplier.name || supplier.supplier_name || "",
          email: supplier.contact_email || supplier.email || "",
        }));
    }

    // Otherwise, try to get from purchase request items
    if (purchaseRequestData?.items) {
      const supplierIds = new Set<number>();
      purchaseRequestData.items.forEach((item: any) => {
        if (item.is_rfq_email_sent && item.supplier) {
          supplierIds.add(item.supplier);
        }
      });

      if (supplierIds.size > 0) {
        return suppliersList
          .filter((supplier: any) => supplierIds.has(supplier.id))
          .map((supplier: any) => ({
            id: supplier.id,
            name: supplier.name || supplier.supplier_name || "",
            email: supplier.contact_email || supplier.email || "",
          }));
      }
    }

    // If no suppliers found via the above methods, show all suppliers
    // (This allows users to select any supplier for comparison)
    return suppliersList.map((supplier: any) => ({
      id: supplier.id,
      name: supplier.name || supplier.supplier_name || "",
      email: supplier.contact_email || supplier.email || "",
    }));
  }, [purchaseRequestData?.items, allSuppliersData, selectedSupplierIds]);

  const filteredSuppliers = React.useMemo(
    () => {
      if (!supplierSearch.trim()) return availableSuppliers;
      const query = supplierSearch.toLowerCase();
      return availableSuppliers.filter((supplier: { name: string; email: string }) =>
        (supplier.name || "").toLowerCase().includes(query) ||
        (supplier.email || "").toLowerCase().includes(query)
      );
    },
    [availableSuppliers, supplierSearch]
  );

  const handleSupplierSelectionForImport = (supplierId: number) => {
    // Single selection: if same supplier clicked, deselect; otherwise select new one
    if (selectedSupplierForImport === supplierId) {
      setSelectedSupplierForImport(null);
    } else {
      setSelectedSupplierForImport(supplierId);
    }
  };

  const handleProceedToImport = () => {
    if (!selectedSupplierForImport) {
      toast.warning("Please select a supplier to import quotations for.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setImportStep("import");
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedSupplierForImport) {
      toast.warning("Please select a supplier first.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsImporting(true);
    setImportErrors([]);

    try {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      let data: any[] = [];

      if (fileExtension === "csv") {
        // Parse CSV
        const text = await file.text();
        const lines = text.split("\n").filter((line) => line.trim());
        const headers = lines[0].split(",").map((h) => h.trim());
        data = lines.slice(1).map((line) => {
          const values = line.split(",").map((v) => v.trim());
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = values[index] || "";
          });
          return obj;
        });
      } else if (fileExtension === "xlsx" || fileExtension === "xls") {
        // Parse Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        data = XLSX.utils.sheet_to_json(worksheet);
      } else {
        throw new Error("Unsupported file format. Please use CSV or Excel files.");
      }

      // Validate and process data
      const errors: string[] = [];
      const updatedSuppliers = [...suppliers];

      if (selectedSupplierForImport) {
        const supplier = availableSuppliers.find(
          (s: { id: number }) => s.id === selectedSupplierForImport,
        );
        if (!supplier) {
          throw new Error("Selected supplier not found");
        }

        // Check if supplier already exists in suppliers list
        let supplierQuotation = updatedSuppliers.find(
          (s) => s.supplier_id === selectedSupplierForImport,
        );

        if (!supplierQuotation) {
          supplierQuotation = {
            supplier_id: supplier.id,
            supplier_name: supplier.name,
            email: supplier.email,
            revisions: [],
          };
          updatedSuppliers.push(supplierQuotation);
        }

        const newRevisionId =
          (supplierQuotation.revisions[supplierQuotation.revisions.length - 1]?.id || 0) + 1;

        const currentRevision: SupplierRevision = {
          id: newRevisionId,
          file_name: file.name,
          quotations: {},
        };
        supplierQuotation.revisions.push(currentRevision);

        // Process each row in the file
        data.forEach((row: any, index: number) => {
          const rowNum = index + 2; // +2 because index starts at 0 and we skip header

          // Prefer to match by Item Code when available
          const itemCode = row["Model Number"] || row["model_number"] || row["Item Code"] || row["item_code"] || "";
          const productName =
            row["Product Name"] ||
            row["product_name"] ||
            row["Product"] ||
            row["product"] ||
            "";

          if (!itemCode && !productName) {
            errors.push(`Row ${rowNum}: Missing product name or model number`);
            return;
          }

          const unitPrice =
            parseFloat(
              row["Unit Price"] ||
                row["unit_price"] ||
                row["Unit price"] ||
                "0",
            ) || 0;
          const totalCost =
            parseFloat(
              row["Total Cost"] ||
                row["total_cost"] ||
                row["Total cost"] ||
                "0",
            ) || 0;

          // Find product by item code first, then by name
          let product =
            itemCode &&
            rfqItems.find(
              (p) =>
                ((p as any).model_number &&
                String((p as any).model_number).toLowerCase() === String(itemCode).toLowerCase()) ||
              ((p as any).item_code &&
                String((p as any).item_code).toLowerCase() ===
                  String(itemCode).toLowerCase()),
            );

          if (!product && productName) {
            product = rfqItems.find(
              (p) =>
                p.product_name.toLowerCase() === productName.toLowerCase() ||
                p.id.toString() === productName ||
                p.product_name.toLowerCase().includes(productName.toLowerCase()) ||
                productName.toLowerCase().includes(p.product_name.toLowerCase()),
            );
          }

          if (!product) {
            errors.push(
              `Row ${rowNum}: Product "${productName || itemCode}" not found in RFQ items`,
            );
            return;
          }

          currentRevision.quotations[product.id.toString()] = {
            unit_price: unitPrice,
            total_cost: totalCost,
            delivery_days:
              parseInt(
                row["Delivery Days"] ||
                  row["delivery_days"] ||
                  row["Delivery"] ||
                  "0",
              ) || undefined,
            notes: row["Notes"] || row["notes"] || row["Note"] || "",
          };
        });
      }

      if (errors.length > 0) {
        setImportErrors(errors);
      }

      setSuppliers(updatedSuppliers);
      setSelectedSuppliersForComparison(
        new Set(updatedSuppliers.map((s) => s.supplier_id)),
      );
      setImportStep("compare");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      setImportErrors([
        `Failed to import file: ${error.message || "Unknown error"}`,
      ]);
    } finally {
      setIsImporting(false);
    }
  };

  const getQuotationForProduct = (
    supplierId: number,
    productId: number,
  ): { unit_price: number; total_cost: number; delivery_days?: number } | null => {
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    if (!supplier) return null;

    const pid = productId.toString();
    let best: { unit_price: number; total_cost: number; delivery_days?: number } | null = null;

    supplier.revisions.forEach((rev) => {
      const q = rev.quotations[pid];
      if (!q) return;
      if (!best || q.total_cost < best.total_cost) {
        best = q;
      }
    });

    return best;
  };

  const getLowestPrice = (productId: number): number | null => {
    let lowest: number | null = null;
    suppliers.forEach((supplier) => {
      const quotation = getQuotationForProduct(supplier.supplier_id, productId);
      if (quotation && quotation.total_cost) {
        if (lowest === null || quotation.total_cost < lowest) {
          lowest = quotation.total_cost;
        }
      }
    });
    return lowest;
  };

  const getTotalCostForSupplier = (supplierId: number): number => {
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    if (!supplier) return 0;

    // Sum of best total cost per product across revisions
    return rfqItems.reduce((sum, product) => {
      const q = getQuotationForProduct(supplierId, product.id);
      return sum + (q?.total_cost || 0);
    }, 0);
  };

  const handleSelectSupplierForComparison = (supplierId: number) => {
    setSelectedSuppliersForComparison((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  // Auto-suggest best supplier per product based on lowest total cost.
  React.useEffect(() => {
    if (importStep !== "compare" || suppliers.length === 0 || rfqItems.length === 0) {
      return;
    }

    const newMap: { [productId: number]: number } = {};

    rfqItems.forEach((product) => {
      const lowest = getLowestPrice(product.id);
      if (lowest === null) {
        return;
      }

      suppliers.forEach((supplier) => {
        const quotation = getQuotationForProduct(supplier.supplier_id, product.id);
        if (quotation && quotation.total_cost === lowest) {
          newMap[product.id] = supplier.supplier_id;
          // First supplier with the lowest price is enough
          return;
        }
      });
    });

    // Auto-select strictly the lowest-priced supplier per product.
    // Manual changes made by the user after this effect will still override.
    setSelectedProductSupplierMap(newMap);
  }, [importStep, suppliers, rfqItems]);

  const handleConfirmSelection = async () => {
    // Build selected product-supplier mappings (winners)
    const selectedMappings = rfqItems.map((product: any) => {
      const supplierId = selectedProductSupplierMap[product.id];
      const quotation =
        supplierId !== undefined && supplierId !== null
          ? getQuotationForProduct(supplierId, product.id)
          : null;

      return {
        purchase_request_item_id: product.id,
        product_id: product.product,
        product_name: product.product_name,
        supplier_id: supplierId,
        quotation,
      };
    });

    const validMappings = selectedMappings.filter((m) => !!m.supplier_id);

    if (validMappings.length === 0) {
      toast.warning("Please select at least one product-supplier mapping.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSavingMappings(true);

    try {
      // 1) Update cost mapping table for ALL suppliers/prices in the grid
      const costMappingPromises: Promise<any>[] = [];

      rfqItems.forEach((product: any) => {
        suppliers.forEach((supplier) => {
          const quotation = getQuotationForProduct(supplier.supplier_id, product.id);
          if (!quotation || !quotation.total_cost) return;

          const payload = {
            supplier: supplier.supplier_id,
            product: product.product, // backend product id on PR item
            total_amount: quotation.total_cost.toString(),
            discount: "0",
          };

          costMappingPromises.push(createSupplierCostMapping(payload).unwrap());
        });
      });

      await Promise.allSettled(costMappingPromises);

      // 2) Apply winners back to PR items (update supplier, price, clear RFQ)
      const applyItems = validMappings.map((m) => {
        return {
          purchase_request_item_id: m.purchase_request_item_id,
          supplier_id: m.supplier_id,
          unit_price: m.quotation?.unit_price?.toString() || "0",
          discount: "0",
        };
      });

      await applyComparisonSelection({
        purchaseRequestId: purchaseRequestId!,
        items: applyItems,
      }).unwrap();

      // Refetch PR so parent screens see updated supplier & RFQ flags
      if (refetchPurchaseRequest) {
        refetchPurchaseRequest();
      }

      toast.success(
        "Comparison selection applied and cost mappings updated.",
        {
          position: "top-right",
          autoClose: 4000,
        }
      );

      setTimeout(() => {
        toggle();
      }, 1500);
    } catch (error: any) {
      console.error("Error saving cost mappings:", error);
      toast.error(
        error?.data?.message || "Failed to save cost mappings. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setIsSavingMappings(false);
    }
  };

  const handleReset = () => {
    setSuppliers([]);
    setSelectedSupplierForImport(null);
    setSelectedSuppliersForComparison(new Set());
    setImportErrors([]);
    setImportStep("select_supplier");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddMoreSuppliers = () => {
    setImportStep("select_supplier");
    setSelectedSupplierForImport(null);
  };

  const handleSelectProductSupplier = (productId: number, supplierId: number, hasQuotation: boolean) => {
    if (!hasQuotation) return;

    setSelectedProductSupplierMap((prev) => {
      const currentSupplierForProduct = prev[productId];
      const newMap = { ...prev };

      // If the same supplier is clicked again, unselect it
      if (currentSupplierForProduct === supplierId) {
        delete newMap[productId];
      } else {
        // Select this supplier for the product (single supplier per product)
        newMap[productId] = supplierId;
      }

      return newMap;
    });
  };

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="supplier-comparison-modal">
      <ModalHeader toggle={toggle} className="bg-gray text-dark">
        <div className="d-flex align-items-center">
          <i className="ri-bar-chart-line me-2 fs-5"></i>
          <div>
            <h5 className="modal-title mb-0 fw-bold">Supplier Comparison</h5>
            <small className="text-dark-50">
              Compare quotations from multiple suppliers
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-0">
        {importStep === "select_supplier" && (
          <div className="p-4">
            <Alert color="info" className="mb-4">
              <h6 className="alert-heading">
                <i className="ri-information-line me-2"></i>
                Step 1: Select Supplier
              </h6>
              <p className="mb-0">
                Select one supplier at a time to import their quotations. 
                After importing, you can add more suppliers for comparison.
              </p>
            </Alert>

            {availableSuppliers.length === 0 ? (
              <Alert color="warning" className="mb-4">
                <i className="ri-alert-line me-2"></i>
                No suppliers available. Please ensure suppliers are loaded.
              </Alert>
            ) : (
              <Card>
                <CardHeader className="bg-light">
                  <h6 className="mb-0">
                    <i className="ri-user-line me-2"></i>
                    Suppliers ({filteredSuppliers.length})
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <InputGroup>
                      <InputGroupText>
                        <i className="ri-search-line" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Search supplier by name or email"
                        value={supplierSearch}
                        onChange={(e) => setSupplierSearch(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                  <div className="row">
                    {filteredSuppliers.map((supplier: { id: number; name: string; email: string }) => (
                      <div key={supplier.id} className="col-md-6 mb-3">
                        <div
                          className={`border rounded p-3 cursor-pointer ${
                            selectedSupplierForImport === supplier.id
                              ? "border-primary bg-primary bg-opacity-10"
                              : "border-secondary"
                          }`}
                          onClick={() => handleSupplierSelectionForImport(supplier.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="flex-grow-1">
                              <div className="fw-bold">{supplier.name}</div>
                              <small className="text-muted d-block">
                                <i className="ri-mail-line me-1"></i>
                                {supplier.email}
                              </small>
                            </div>
                            <FormGroup check className="mb-0 ms-3">
                              <Input
                                type="radio"
                                name="supplier-selection"
                                checked={selectedSupplierForImport === supplier.id}
                                onChange={() => handleSupplierSelectionForImport(supplier.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </FormGroup>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedSupplierForImport && (
                    <Alert color="success" className="mt-3">
                      <i className="ri-check-line me-2"></i>
                      {availableSuppliers.find((s: { id: number }) => s.id === selectedSupplierForImport)?.name} selected. 
                      Click "Proceed to Import" to upload quotation file.
                    </Alert>
                  )}
                </CardBody>
              </Card>
            )}
          </div>
        )}

        {importStep === "import" && (
          <div className="p-4">
            <Alert color="info" className="mb-4">
              <h6 className="alert-heading">
                <i className="ri-information-line me-2"></i>
                Step 2: Import Quotation File
              </h6>
              <p className="mb-0">
                Import the quotation file for the selected supplier(s). 
                The file should contain the following columns:
              </p>
              <ul className="mb-0 mt-2">
                <li>
                  <strong>Product Name</strong> - Name or ID of the product
                </li>
                <li>
                  <strong>Unit Price</strong> - Price per unit
                </li>
                <li>
                  <strong>Total Cost</strong> - Total cost for the quantity
                </li>
                <li>
                  <strong>Delivery Days</strong> (optional) - Delivery timeline
                </li>
                <li>
                  <strong>Notes</strong> (optional) - Additional notes
                </li>
              </ul>
            </Alert>

            {/* Show selected supplier */}
            {selectedSupplierForImport && (
              <Alert color="primary" className="mb-3">
                <strong>Importing for:</strong>{" "}
                {availableSuppliers.find((s: { id: number }) => s.id === selectedSupplierForImport)?.name || "Unknown Supplier"}
              </Alert>
            )}

            <Card>
              <CardHeader className="bg-light">
                <h6 className="mb-0">
                  <i className="ri-upload-cloud-line me-2"></i>
                  Import Quotation File
                </h6>
              </CardHeader>
              <CardBody>
                <FormGroup>
                  <Label for="quotationFile">
                    Select Excel or CSV File <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="file"
                    id="quotationFile"
                    innerRef={fileInputRef}
                    onChange={handleFileImport}
                    accept=".xlsx,.xls,.csv"
                    disabled={isImporting}
                  />
                  <div className="form-text">
                    Supported formats: Excel (.xlsx, .xls) or CSV (.csv)
                  </div>
                </FormGroup>

                {isImporting && (
                  <div className="text-center py-3">
                    <Spinner color="primary" className="me-2" />
                    <span>Processing file...</span>
                  </div>
                )}

                {importErrors.length > 0 && (
                  <Alert color="danger" className="mt-3">
                    <h6 className="alert-heading">
                      <i className="ri-error-warning-line me-2"></i>
                      Import Errors ({importErrors.length})
                    </h6>
                    <ul className="mb-0" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {importErrors.map((error, index) => (
                        <li key={index} className="small">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </div>
        )}

        {importStep === "compare" && suppliers.length > 0 && (
          <div className="p-4">
            {/* Summary Cards */}
            <div className="row mb-4">
              <div className="col-md-4">
                <Card className="border-primary">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Suppliers</h6>
                    <h3 className="text-primary mb-0">{suppliers.length}</h3>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-4">
                <Card className="border-success">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Products</h6>
                    <h3 className="text-success mb-0">{rfqItems.length}</h3>
                  </CardBody>
                </Card>
              </div>
              <div className="col-md-4">
                <Card className="border-warning">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Selected Mappings</h6>
                    <h3 className="text-warning mb-0">
                      {Object.keys(selectedProductSupplierMap).length}
                    </h3>
                  </CardBody>
                </Card>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="table-responsive" style={{ maxHeight: "500px" }}>
              <Table hover bordered className="mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th
                      rowSpan={2}
                      style={{ minWidth: "200px" }}
                      className="align-middle"
                    >
                      Product Details
                    </th>
                    {suppliers.map((supplier) => (
                      <th
                        key={supplier.supplier_id}
                        colSpan={4}
                        className="text-center"
                        style={{ minWidth: "260px" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <div className="fw-bold">{supplier.supplier_name}</div>
                            <small className="text-muted d-block">
                              {supplier.email}
                            </small>
                            {supplier.revisions.map((rev, idx) => (
                              <small key={rev.id} className="text-muted d-block">
                                Rev {idx + 1}: {rev.file_name}
                              </small>
                            ))}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <FormGroup check className="mb-0">
                              <Input
                                type="checkbox"
                                checked={selectedSuppliersForComparison.has(supplier.supplier_id)}
                                onChange={() =>
                                  handleSelectSupplierForComparison(supplier.supplier_id)
                                }
                              />
                            </FormGroup>
                            <Button
                              size="sm"
                              color="link"
                              className="text-danger p-0"
                              onClick={() => {
                                // Remove supplier column and its mappings
                                setSuppliers((prev) =>
                                  prev.filter((s) => s.supplier_id !== supplier.supplier_id)
                                );
                                setSelectedSuppliersForComparison((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(supplier.supplier_id);
                                  return newSet;
                                });
                                setSelectedProductSupplierMap((prev) => {
                                  const newMap: { [productId: number]: number } = {};
                                  Object.entries(prev).forEach(([productId, supId]) => {
                                    if (supId !== supplier.supplier_id) {
                                      newMap[Number(productId)] = supId;
                                    }
                                  });
                                  return newMap;
                                });
                              }}
                            >
                              <i className="ri-close-circle-line" />
                            </Button>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {suppliers.map((supplier) => (
                      <React.Fragment key={supplier.supplier_id}>
                        <th className="text-center small">Select</th>
                        <th className="text-center small">Unit Price</th>
                        <th className="text-center small">Total Cost</th>
                        <th className="text-center small">Delivery</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rfqItems.map((product) => {
                    const lowestPrice = getLowestPrice(product.id);
                    return (
                      <tr key={product.id}>
                        <td>
                          <div>
                            <div className="fw-semibold">{product.product_name}</div>
                            <small className="text-muted">
                              Qty: {product.quantity} {product.measurement_name}
                            </small>
                          </div>
                        </td>
                        {suppliers.map((supplier) => {
                          const quotation = getQuotationForProduct(
                            supplier.supplier_id,
                            product.id
                          );
                          const isLowest =
                            quotation &&
                            lowestPrice !== null &&
                            quotation.total_cost === lowestPrice;
                          const hasQuotation = quotation !== null;

                          return (
                            <React.Fragment key={supplier.supplier_id}>
                              <td className="text-center align-middle">
                                {hasQuotation ? (
                                  <FormGroup
                                    check
                                    className="mb-0 d-flex justify-content-center"
                                  >
                                    <Input
                                      type="checkbox"
                                      checked={
                                        selectedProductSupplierMap[product.id] ===
                                        supplier.supplier_id
                                      }
                                      onChange={() =>
                                        handleSelectProductSupplier(
                                          product.id,
                                          supplier.supplier_id,
                                          hasQuotation
                                        )
                                      }
                                    />
                                  </FormGroup>
                                ) : (
                                  <span className="fst-italic text-muted">—</span>
                                )}
                              </td>
                              <td
                                className={`text-center ${
                                  hasQuotation
                                    ? isLowest
                                      ? "bg-success bg-opacity-10 fw-bold text-success"
                                      : ""
                                    : "bg-danger bg-opacity-10 text-muted"
                                }`}
                              >
                                {hasQuotation ? (
                                  <span>${quotation.unit_price.toFixed(2)}</span>
                                ) : (
                                  <span className="fst-italic">—</span>
                                )}
                              </td>
                              <td
                                className={`text-center ${
                                  hasQuotation
                                    ? isLowest
                                      ? "bg-success bg-opacity-10 fw-bold text-success"
                                      : ""
                                    : "bg-danger bg-opacity-10 text-muted"
                                }`}
                              >
                                {hasQuotation ? (
                                  <div>
                                    <div>${quotation.total_cost.toFixed(2)}</div>
                                    {isLowest && (
                                      <Badge color="success" className="small">
                                        Lowest
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span className="fst-italic">—</span>
                                )}
                              </td>
                              <td
                                className={`text-center ${
                                  hasQuotation
                                    ? ""
                                    : "bg-danger bg-opacity-10 text-muted"
                                }`}
                              >
                                {hasQuotation ? (
                                  quotation.delivery_days ? (
                                    <Badge color="info">
                                      {quotation.delivery_days} days
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )
                                ) : (
                                  <span className="fst-italic">—</span>
                                )}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <th className="fw-bold">Total</th>
                    {suppliers.map((supplier) => {
                      const total = getTotalCostForSupplier(supplier.supplier_id);
                      const allTotals = suppliers.map((s) =>
                        getTotalCostForSupplier(s.supplier_id)
                      );
                      const isLowestTotal =
                        total > 0 && total === Math.min(...allTotals.filter((t) => t > 0));

                      return (
                        <td
                          key={supplier.supplier_id}
                          colSpan={4}
                          className={`text-center fw-bold ${
                            isLowestTotal
                              ? "bg-success bg-opacity-25 text-success"
                              : ""
                          }`}
                        >
                          ${total.toFixed(2)}
                          {isLowestTotal && (
                            <Badge color="success" className="ms-2">
                              Best Price
                            </Badge>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Cost Variation Summary */}
            {suppliers.length > 1 && (
              <Card className="mt-4 border-info">
                <CardHeader className="bg-info bg-opacity-10">
                  <h6 className="mb-0">
                    <i className="ri-line-chart-line me-2"></i>
                    Cost Variation Summary
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="row">
                    {suppliers.map((supplier) => {
                      const total = getTotalCostForSupplier(supplier.supplier_id);
                      const allTotals = suppliers
                        .map((s) => getTotalCostForSupplier(s.supplier_id))
                        .filter((t) => t > 0);
                      const maxTotal = Math.max(...allTotals);
                      const minTotal = Math.min(...allTotals);
                      const variation =
                        maxTotal > 0
                          ? ((total - minTotal) / minTotal) * 100
                          : 0;

                      return (
                        <div key={supplier.supplier_id} className="col-md-4 mb-3">
                          <div className="border rounded p-3">
                            <div className="fw-bold">{supplier.supplier_name}</div>
                            <div className="h5 text-primary mb-1">
                              ${total.toFixed(2)}
                            </div>
                            {total > 0 && (
                              <small className="text-muted">
                                {variation > 0
                                  ? `+${variation.toFixed(1)}% from lowest`
                                  : "Lowest price"}
                              </small>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="bg-light">
        <div className="d-flex justify-content-between w-100">
          <div>
            {importStep === "compare" && (
              <Button color="outline-secondary" onClick={handleReset}>
                <i className="ri-refresh-line me-1"></i>
                Import New File
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            {importStep === "compare" &&
              rfqItems.some((product: any) => !!selectedProductSupplierMap[product.id]) && (
              <Button
                color="success"
                onClick={handleConfirmSelection}
                className="me-2"
                disabled={isSavingMappings}
              >
                {isSavingMappings ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving Mappings...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line me-1"></i>
                    Confirm Selection
                  </>
                )}
              </Button>
            )}
            {importStep === "select_supplier" && (
              <Button
                color="primary"
                onClick={handleProceedToImport}
                disabled={!selectedSupplierForImport}
              >
                <i className="ri-arrow-right-line me-1"></i>
                Proceed to Import
              </Button>
            )}
            {importStep === "import" && (
              <Button
                color="outline-secondary"
                onClick={() => setImportStep("select_supplier")}
                className="me-2"
              >
                <i className="ri-arrow-left-line me-1"></i>
                Back to Supplier Selection
              </Button>
            )}
            {importStep === "compare" && (
              <Button
                color="outline-primary"
                onClick={handleAddMoreSuppliers}
                className="me-2"
              >
                <i className="ri-add-line me-1"></i>
                Add More Suppliers
              </Button>
            )}
            <Button color="warning" onClick={toggle}>
              <i className="ri-close-line me-1"></i>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SupplierComparisonModal;
